import React, { Component } from "react";
import {
  Alert,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { shape, string, func } from "prop-types";
import { withRouter } from "react-router-dom";
import swal from "sweetalert2";
import LoadingOverlay from "../../Components/LoadingOverlay";
import { Formik } from "formik";
import LimitInput from "./LimitInput";
import limitsApi from "../../../api/limits";
import { dateFormatter } from "../../../util/formatter";
import { handleError } from "@/util/errorHandler";
import { getLimitSchema } from "@/util/validationHelper";

const warningStyle = {
  color: "red",
  fontSize: 10,
  height: 25,
  paddingTop: 5,
};

class EditLimitComponent extends Component {
  static propTypes = {
    match: shape({ params: shape({ limitId: string.isRequired }) }).isRequired,
    history: shape({ goBack: func.isRequired }).isRequired,
  };

  state = {
    isInitialLoading: true,
    isSending: false,
    limitData: {},
  };

  async componentDidMount() {
    const { limitId } = this.props.match.params;

    try {
      await limitsApi
        .getLimitById(limitId)
        .then(response => this.setState({ limitData: response }));
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isInitialLoading: false });
    }
  }

  validationSchema() {
    const { limitData } = this.state;

    return getLimitSchema({ limitData });
  }

  onSubmit = async ({ KHR, USD, limitName, description }) => {
    const { limitData } = this.state;

    this.setState({ isSending: true });

    try {
      return await limitsApi
        .changeLimit({
          limitId: limitData.id,
          limitName,
          description,
          limits: {
            "khr#nbc": {
              dayLimit: KHR,
            },
            "usd#nbc": {
              dayLimit: USD,
            },
          },
        })
        .then(() =>
          swal(
            "Success",
            `${limitData.limitName} changed successfully`,
            "success",
          ),
        );
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ isSending: false });
    }
  };

  goBack = () => {
    this.props.history.goBack();
  };

  render() {
    const { limitData, isInitialLoading, isSending } = this.state;

    if (isInitialLoading) {
      return <LoadingOverlay loading />;
    }

    return (
      <Formik
        initialValues={{
          KHR:
            limitData.limits && limitData.limits["khr#nbc"]
              ? limitData.limits["khr#nbc"].dayLimit
              : 0,
          USD:
            limitData.limits && limitData.limits["usd#nbc"]
              ? limitData.limits["usd#nbc"].dayLimit
              : 0,
          limitName: limitData.limitName,
          description: limitData.description || "",
        }}
        validationSchema={this.validationSchema()}
        onSubmit={this.onSubmit}
        enableReinitialize>
        {({ values, handleSubmit, handleChange, errors, touched }) => (
          <Form onSubmit={handleSubmit}>
            <Card>
              <LoadingOverlay loading={isSending} />
              <CardHeader>{limitData.limitName}</CardHeader>
              <CardBody>
                {!limitData.customLimit && (
                  <Alert color="danger">You can't edit NBC limits</Alert>
                )}

                <FormGroup row>
                  <Label sm={2}>Name:</Label>
                  <Col sm={3}>
                    <Input
                      type="text"
                      name="limitName"
                      value={values.limitName}
                      onChange={handleChange}
                      disabled={!limitData.customLimit}
                    />
                    <div style={warningStyle}>{errors.name}</div>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label sm={2}>KYC Status:</Label>
                  <Col sm={3}>
                    <Input type="text" value={limitData.kycStatus} disabled />
                    <div style={warningStyle} />
                  </Col>
                </FormGroup>
                <div style={{ marginBottom: 30 }}>
                  Please input maximum amount of transactions
                </div>
                <FormGroup row>
                  <Label sm={2}>Amount in KHR:</Label>
                  <Col sm={5}>
                    <LimitInput
                      currency="KHR"
                      value={values.KHR}
                      onChange={handleChange}
                      invalid={Boolean(errors.KHR)}
                      disabled={!limitData.customLimit}
                    />
                    <div style={warningStyle}>{errors.KHR}</div>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label sm={2}>Amount in USD:</Label>
                  <Col sm={5}>
                    <LimitInput
                      currency="USD"
                      value={values.USD}
                      onChange={handleChange}
                      invalid={Boolean(errors.USD)}
                      disabled={!limitData.customLimit}
                    />
                    <div style={warningStyle}>{errors.USD}</div>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label sm={2}>Description:</Label>
                  <Col sm={8}>
                    <Input
                      type="textarea"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      disabled={!limitData.customLimit}
                    />
                    <div style={warningStyle} />
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label sm={2}>Time created:</Label>
                  <Col sm={3}>
                    <Input
                      type="text"
                      value={dateFormatter(limitData.createdTime)}
                      disabled
                    />
                    <div style={warningStyle} />
                  </Col>
                  <Label sm={2}>Time updated:</Label>
                  <Col sm={3}>
                    <Input
                      type="text"
                      value={dateFormatter(limitData.updatedTime)}
                      disabled
                    />
                    <div style={warningStyle} />
                  </Col>
                </FormGroup>
              </CardBody>
              <CardFooter>
                <Button
                  color="success"
                  type="submit"
                  data-cy="submit"
                  disabled={!limitData.customLimit}>
                  Submit
                </Button>
                <Button
                  color="primary"
                  style={{ marginLeft: 20 }}
                  onClick={this.goBack}
                  data-cy="back">
                  <i className="fa fa-arrow-left mr-2 " />
                  Back
                </Button>
              </CardFooter>
            </Card>
          </Form>
        )}
      </Formik>
    );
  }
}

const EditLimit = withRouter(EditLimitComponent);

export { EditLimit };
